/**
 * Collect carton weight and missing country of origin, then download the transfer commercial invoice.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

type DraftLine = {
  sku: string;
  description: string;
  quantity: number;
  unitCost: number;
  htsCode: string;
  countryOfOrigin: string | null;
  unitOfMeasure: string;
};

type DraftBox = {
  id: number;
  boxNumber: number;
  weightLbs: number | null;
  lengthIn: number | null;
  widthIn: number | null;
  heightIn: number | null;
};

type Draft = {
  invoiceNumber: string;
  destinationLabel: string;
  incoterms: string;
  reasonForExport: string;
  lines: DraftLine[];
  boxes: DraftBox[];
};

function money(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function downloadPdf(base64: string, filename: string): void {
  const link = document.createElement("a");
  link.href = `data:application/pdf;base64,${base64}`;
  link.download = filename;
  link.click();
}

export function TransferCommercialInvoiceModal({
  transferOrderId,
  transferNumber,
  onClose,
}: {
  transferOrderId: number;
  transferNumber: string | null;
  onClose: () => void;
}): JSX.Element {
  const { showToast } = useHubToast();
  const title = transferNumber?.trim() || `Transfer #${String(transferOrderId)}`;
  const [origins, setOrigins] = useState<Record<string, string>>({});
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [looseWeight, setLooseWeight] = useState("");

  const draftQuery = useQuery<{ draft: Draft }, ApiError>({
    queryKey: ["manufacturing", "transfer-commercial-invoice", transferOrderId],
    queryFn: () =>
      apiFetch<{ draft: Draft }>(`/api/manufacturing/transfers/${String(transferOrderId)}/commercial-invoice`),
  });

  useEffect(() => {
    const draft = draftQuery.data?.draft;
    if (draft == null) return;
    const nextOrigins: Record<string, string> = {};
    for (const line of draft.lines) {
      nextOrigins[line.sku] = line.countryOfOrigin ?? "";
    }
    setOrigins(nextOrigins);
    const nextWeights: Record<string, string> = {};
    for (const box of draft.boxes) {
      nextWeights[String(box.id)] = box.weightLbs == null ? "" : String(box.weightLbs);
    }
    setWeights(nextWeights);
  }, [draftQuery.data]);

  const printMutation = useMutation<{ pdfBase64: string; filename: string }, ApiError>({
    mutationFn: () => {
      const draft = draftQuery.data?.draft;
      if (draft == null) {
        throw new ApiError(400, "Commercial invoice details are still loading.");
      }
      const countryOfOriginBySku: Record<string, string> = {};
      for (const line of draft.lines) {
        countryOfOriginBySku[line.sku] = (origins[line.sku] ?? "").trim();
      }
      const boxes = draft.boxes.map((box) => ({
        id: box.id,
        weightLbs: Number(weights[String(box.id)]),
      }));
      return apiFetch<{ pdfBase64: string; filename: string }>(
        `/api/manufacturing/transfers/${String(transferOrderId)}/commercial-invoice-pdf`,
        {
          method: "POST",
          body: {
            boxes,
            countryOfOriginBySku,
            grossWeightLbs: draft.boxes.length === 0 ? Number(looseWeight) : null,
          },
        },
      );
    },
    onSuccess: (result) => {
      downloadPdf(result.pdfBase64, result.filename);
      showToast({ tone: "success", message: "Commercial invoice downloaded." });
      onClose();
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const draft = draftQuery.data?.draft;

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-commercial-invoice-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <h2 id="transfer-commercial-invoice-title" className="text-lg font-semibold text-slate-900">
              Commercial invoice — {title}
            </h2>
            <HoverHintWithGlyph hoverHint="Temporary export. Delivered duty paid by Smart Grid Solutions. Declared value is hub unit cost. The invoice date is today. Reprint it on the day the goods leave if that day is later. Country of origin should match the package label." />
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
          {draftQuery.isLoading ? (
            <p className="text-sm text-slate-600">Loading commercial invoice…</p>
          ) : draftQuery.isError ? (
            <p className="text-sm text-rose-700">{draftQuery.error.message}</p>
          ) : draft == null ? null : (
            <>
              <p className="text-sm text-slate-700">
                {draft.destinationLabel}. {draft.incoterms}
              </p>
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="border-b border-slate-200 px-2 py-2">SKU</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Qty</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Unit cost</th>
                    <th className="border-b border-slate-200 px-2 py-2">Tariff code</th>
                    <th className="border-b border-slate-200 px-2 py-2">Country of origin</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.lines.map((line) => (
                    <tr key={line.sku}>
                      <td className="border-b border-slate-100 px-2 py-2">
                        <div className="font-medium text-slate-900">{line.sku}</div>
                        <div className="text-xs text-slate-600">{line.description}</div>
                      </td>
                      <td className="border-b border-slate-100 px-2 py-2 text-right tabular-nums">
                        {line.quantity.toLocaleString("en-US")}
                      </td>
                      <td className="border-b border-slate-100 px-2 py-2 text-right tabular-nums">
                        {money(line.unitCost)}
                      </td>
                      <td className="border-b border-slate-100 px-2 py-2 font-mono text-xs">{line.htsCode}</td>
                      <td className="border-b border-slate-100 px-2 py-2">
                        <Label htmlFor={`origin-${line.sku}`} className="sr-only">
                          Country of origin for {line.sku}
                        </Label>
                        <Input
                          id={`origin-${line.sku}`}
                          value={origins[line.sku] ?? ""}
                          onChange={(event) =>
                            setOrigins((current) => ({ ...current, [line.sku]: event.target.value }))
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {draft.boxes.length === 0 ? (
                <div className="max-w-xs">
                  <Label htmlFor="transfer-ci-gross-weight">Gross weight (pounds)</Label>
                  <Input
                    id="transfer-ci-gross-weight"
                    className="mt-1"
                    inputMode="decimal"
                    value={looseWeight}
                    onChange={(event) => setLooseWeight(event.target.value)}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  {draft.boxes.map((box) => (
                    <div key={box.id} className="max-w-xs">
                      <Label htmlFor={`weight-${String(box.id)}`}>
                        Carton {String(box.boxNumber)} gross weight (pounds)
                      </Label>
                      <Input
                        id={`weight-${String(box.id)}`}
                        className="mt-1"
                        inputMode="decimal"
                        value={weights[String(box.id)] ?? ""}
                        onChange={(event) =>
                          setWeights((current) => ({ ...current, [String(box.id)]: event.target.value }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={draftQuery.isLoading || draftQuery.isError || printMutation.isPending}
            onClick={() => printMutation.mutate()}
          >
            {printMutation.isPending ? "Preparing…" : "Download invoice"}
          </Button>
        </div>
      </div>
    </div>
  );
}
